import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { TutorDrawer } from "@/components/TutorDrawer";
import Nfpa70Modal from "@/components/Nfpa70Modal";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Building2, BookOpen, Zap, ShieldCheck, ClipboardCheck } from "lucide-react";

export default function S98Review() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(0);
  const [nfpa70Open, setNfpa70Open] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/s98/review");
        setData(d);
      } catch {
        toast.error("Couldn't load S-98 review");
      }
    })();
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-5xl mx-auto px-6 py-20 text-zinc-500">Loading S-98 review…</div>
      </div>
    );
  }

  const section = data.sections[active];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid="s98-review-page">
        <Link to="/dashboard" className="text-sm text-zinc-600 hover:text-red-600 inline-flex items-center gap-1 mb-6">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </Link>

        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-8 mb-12">
          <div className="lg:col-span-8">
            <div className="overline mb-3"><span className="red-bar" />NYC Review Module</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter" data-testid="s98-title">
              {data.title}
            </h1>
            <div className="font-mono text-xs text-red-600 uppercase tracking-[0.2em] mt-3">{data.subtitle}</div>
            <p className="text-zinc-700 leading-relaxed mt-5 max-w-3xl">{data.intro}</p>
          </div>
          <div className="lg:col-span-4">
            <div className="card-tech p-6" data-testid="s98-exam-facts">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-4 h-4 text-red-600" />
                <div className="overline">Exam facts</div>
              </div>
              <ul className="space-y-3 text-sm">
                {data.exam_facts.map((f, i) => (
                  <li key={i} className="border-b border-zinc-100 last:border-0 pb-3 last:pb-0">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{f.label}</div>
                    <div className="text-zinc-900 font-semibold mt-0.5">{f.value}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Sections tabs */}
        <div className="grid lg:grid-cols-12 gap-8 mb-12">
          <aside className="lg:col-span-3" data-testid="s98-sidebar">
            <div className="overline mb-3"><span className="red-bar" />Review topics</div>
            <nav className="space-y-1">
              {data.sections.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => setActive(i)}
                  className={`w-full text-left px-3 py-2.5 text-sm border-l-2 transition-all rounded-none ${
                    active === i
                      ? "border-red-600 bg-red-50 text-red-700 font-semibold"
                      : "border-zinc-200 text-zinc-700 hover:border-zinc-400 hover:bg-zinc-50"
                  }`}
                  data-testid={`s98-tab-${i}`}
                >
                  {s.title}
                </button>
              ))}
            </nav>
          </aside>

          <article className="lg:col-span-9" data-testid={`s98-section-${section.id}`}>
            <div className="card-tech p-8 md:p-10">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen className="w-4 h-4 text-red-600" />
                <div className="overline">Section {active + 1} of {data.sections.length}</div>
              </div>
              <h2 className="font-heading text-3xl font-bold tracking-tight mb-5" data-testid="s98-section-title">{section.title}</h2>
              <div className="lesson-prose">
                <p>{section.body}</p>
              </div>

              <div className="mt-10 pt-6 border-t border-zinc-200 flex items-center justify-between">
                <button
                  onClick={() => setActive((a) => Math.max(0, a - 1))}
                  disabled={active === 0}
                  className="btn-outline text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="s98-prev-btn"
                >
                  <ArrowLeft className="w-4 h-4" /> Previous
                </button>
                {active < data.sections.length - 1 ? (
                  <button
                    onClick={() => setActive((a) => Math.min(data.sections.length - 1, a + 1))}
                    className="btn-primary text-sm"
                    data-testid="s98-next-btn"
                  >
                    Next section <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <Link to={`/quiz/${data.quiz_id}`} data-testid="s98-take-quiz-btn">
                    <button className="btn-primary text-sm">
                      <ClipboardCheck className="w-4 h-4" /> Take the S-98 quiz <ArrowRight className="w-4 h-4" />
                    </button>
                  </Link>
                )}
              </div>
            </div>
          </article>
        </div>

        {/* Highlight bento */}
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <Link to="/lesson/m6-l1" className="card-tech p-6 block group hover:border-red-600 transition-colors" data-testid="s98-firecode-card">
            <Zap className="w-5 h-5 text-red-600 mb-3" />
            <div className="overline">NYC Fire Code</div>
            <div className="font-heading text-lg font-bold mt-1 tracking-tight group-hover:text-red-600 transition-colors">Chapter 9 governs systems</div>
            <div className="text-xs text-zinc-600 mt-2 leading-relaxed">§907 covers required systems, components, inspection, testing, and impairment reporting for FDNY-jurisdiction buildings.</div>
            <div className="text-xs font-semibold text-red-600 mt-3 inline-flex items-center gap-1">
              Open Module 6 · L1 <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </Link>
          <button
            type="button"
            onClick={() => setNfpa70Open(true)}
            className="card-tech p-6 block text-left group hover:border-red-600 transition-colors w-full"
            data-testid="s98-electrical-card"
          >
            <ShieldCheck className="w-5 h-5 text-red-600 mb-3" />
            <div className="overline">NYC Electrical Code</div>
            <div className="font-heading text-lg font-bold mt-1 tracking-tight group-hover:text-red-600 transition-colors">NFPA 70 + NYC amendments</div>
            <div className="text-xs text-zinc-600 mt-2 leading-relaxed">Article 760 + NYC-specific red-cable, metal-raceway, and SPD surge-protection requirements.</div>
            <div className="text-xs font-semibold text-red-600 mt-3 inline-flex items-center gap-1">
              View NFPA 70 reference <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </button>
          <Link to={`/quiz/${data.quiz_id}`} className="card-tech p-6 block group hover:border-red-600 transition-colors" data-testid="s98-quiz-card">
            <ClipboardCheck className="w-5 h-5 text-red-600 mb-3" />
            <div className="overline">S-98 practice quiz</div>
            <div className="font-heading text-lg font-bold mt-1 tracking-tight group-hover:text-red-600 transition-colors">20 FDNY-style questions</div>
            <div className="text-xs text-zinc-600 mt-2 leading-relaxed">Timed, FDNY-style multiple choice. Drill until consistent on Chapter 9 + NFPA 70 NYC amendments.</div>
            <div className="text-xs font-semibold text-red-600 mt-3 inline-flex items-center gap-1" data-testid="s98-quiz-shortcut">
              Open the quiz <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </Link>
        </div>
      </main>

      <TutorDrawer lessonTitle={`S-98: ${section.title}`} />
      <Nfpa70Modal open={nfpa70Open} onClose={() => setNfpa70Open(false)} />
    </div>
  );
}
